import { debounce } from "@solid-primitives/scheduled";
import { LabelAssociationsSyncRequestConfig, LabelsSyncRequestConfig, useStorytellClient } from "@storytell/client";
import { subscribeToGlobalEvent, subscribeToServerEvent } from "@storytell/events";
import type { Label, LabelAssociation } from "@storytell/types";
import { createEffect, createResource, createSignal, on, onCleanup, onMount, startTransition } from "solid-js";
import { isServer } from "solid-js/web";
import { createResourceStorage } from "../../lib/createResourceStorage";
import { createEntitySyncManager } from "../sync/createEntitySyncManager";

export type LabelsService = ReturnType<typeof createLabelsService>;

export const createLabelsService = (deps: {
  authReady: () => boolean;
  authToken: () => string | undefined;
  userId: () => string | undefined;
  projectId: () => string | undefined;
  onAssociationsChanged?: () => void;
}) => {
  const client = useStorytellClient();

  const labels = createEntitySyncManager<Label>((label) => [label.name, label.description]);

  const storage = createResourceStorage(
    () => labels.all(),
    (value) => labels.hydrateFromSync(value || [])
  );

  // projectId is the reactive key: returning it gates the request on
  // X-Project-Id and triggers a refetch on project navigation.
  const source = () => {
    if (isServer || !deps.authReady() || !deps.userId()) return false;
    return deps.projectId() ?? false;
  };

  // The resource carries initialValue [], so a store that has never synced reads exactly like an
  // empty one. Anything that distinguishes "no labels" from "not fetched yet" needs this.
  const [synced, setSynced] = createSignal(false);

  // The resource's value is consumed through `storage` below, not this binding.
  const [, { refetch }] = createResource(
    source,
    async (): Promise<Label[]> => {
      const request = LabelsSyncRequestConfig.withBearerToken(deps.authToken());
      const response = await client.fetch(request, undefined, undefined);
      setSynced(true);
      if (response.isErr()) {
        console.error("Failed to fetch labels sync", response.error);
        return [];
      }
      setSynced(true);

      const data = Object.values(response.value.labels);
      if (isServer) {
        storage[1](data);
      }
      return data;
    },
    {
      initialValue: [] as Label[],
      deferStream: true,
      storage: () => storage,
    }
  );

  const [labelAssociations, { refetch: refetchLabelAssociations, mutate: mutateLabelAssociations }] = createResource(
    source,
    async (_, { value, refetching }): Promise<LabelAssociation[]> => {
      const request = LabelAssociationsSyncRequestConfig.withBearerToken(deps.authToken());
      const response = await client.fetch(request, undefined, undefined);
      if (response.isErr()) {
        console.error("Failed to fetch labels sync", response.error);
        // A failed refetch keeps the last good list, or every label on every asset vanishes
        // until the next event. A new project starts empty rather than keep the old one's.
        return refetching ? (value ?? []) : [];
      }
      return response.value as LabelAssociation[];
    },
    {
      initialValue: [],
      deferStream: true,
    }
  );
  // Runs once the list is stored, so the listener reads the new list rather than the one before.
  createEffect(
    on(
      () => labelAssociations.latest,
      () => deps.onAssociationsChanged?.(),
      { defer: true }
    )
  );

  // Debounced refetch function wrapped in startTransition
  const debouncedRefetch = debounce(() => {
    void startTransition(() => {
      void refetch();
    });
  }, 750);
  // Associations have their own feed: attaching or removing a label writes an
  // association, not a label, so a teammate's labelling reaches this list only here.
  const debouncedRefetchLabelAssociations = debounce(() => {
    void startTransition(() => {
      void refetchLabelAssociations();
    });
  }, 750);

  onMount(() => {
    const unsubscribeUserContext = subscribeToGlobalEvent("user-context-changed", () => {
      void refetch();
    });
    const unsubscribeConnectivityRestored = subscribeToGlobalEvent("connectivity-restored", () => {
      void refetch();
      void refetchLabelAssociations();
    });
    const unsubscribeServerEvent = subscribeToServerEvent("cp.labels", () => {
      debouncedRefetch();
    });
    // Removing a label from an asset soft-deletes its association, which arrives as
    // op.update, so every op refetches.
    const unsubscribeAssociationsEvent = subscribeToServerEvent("cp.label_associations", () => {
      debouncedRefetchLabelAssociations();
    });
    onCleanup(() => {
      unsubscribeUserContext();
      unsubscribeConnectivityRestored();
      unsubscribeServerEvent();
      unsubscribeAssociationsEvent();
      debouncedRefetch.clear();
      debouncedRefetchLabelAssociations.clear();
    });
  });

  const getAssociation = (labelId: string, resourceId: string) => {
    return labelAssociations.latest.find(
      (association) => association.labelId === labelId && association.resourceId === resourceId
    );
  };

  const onLabelAssociated = (association: LabelAssociation) => {
    mutateLabelAssociations((associations) => [...associations, association]);
  };
  const onLabelDisassociated = (associationId: string) => {
    mutateLabelAssociations((associations) => associations.filter((association) => association.id !== associationId));
  };

  /** Snapshot for rels cache rebuild (asset list API may omit per-asset `labels`). */
  const getLatestLabelAssociations = (): LabelAssociation[] => labelAssociations.latest;

  return {
    synced,
    refetchLabelAssociations,
    getAssociation,
    getLatestLabelAssociations,
    onLabelAssociated,
    onLabelDisassociated,
    ...labels,
  };
};
